import React, { useEffect, useState, useCallback, useRef } from "react";
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { Badge } from "../ui/badge";

// Single source of truth for provider wiring. Do NOT expose provider name in
// user-facing copy — only the URL / origin live in code.
export const COVERAGE_IFRAME_SRC = "https://www.wd.likestelecom.com/coverage/public/general";
// Derive the allowed postMessage origin from the iframe URL itself so the
// two can never drift out of sync when the provider domain changes.
// If `URL` parsing ever fails (never in supported browsers), fall back to a
// value that rejects everything.
export const COVERAGE_ALLOWED_ORIGIN = (() => {
  try {
    return new URL(COVERAGE_IFRAME_SRC).origin;
  } catch {
    return "about:blank";
  }
})();

export const EMPTY_COVERAGE = { status: null, address: "", vertical: "", gescal: "" };

// Some providers send X-Frame-Options / CSP `frame-ancestors` that block
// embedding from non-allowlisted origins. Browsers can't surface that from
// JavaScript (cross-origin), so we always offer an "open in new tab" escape
// hatch and, if the iframe's onLoad never fires within this window, we show
// an explicit warning. Kept generous to avoid false positives on slow links.
const IFRAME_LOAD_TIMEOUT_MS = 8000;

/**
 * Shared coverage iframe + postMessage listener + result panel.
 *
 * Two callers today:
 *   1. CoverageSearchPage — standalone free search, no persistence.
 *   2. OpportunityCoverageModal — persists the result onto the opportunity.
 *
 * The component itself never saves; it surfaces the latest result via the
 * `onResult` callback so the caller decides what to do with it.
 */
const CoverageSearchEmbed = ({ initialResult = EMPTY_COVERAGE, onResult, iframeHeight = "700px" }) => {
  const [result, setResult] = useState(initialResult || EMPTY_COVERAGE);
  // Track iframe loading state so the user always has feedback. `loading`
  // flips to false on `onLoad` *or* on the timeout fallback; `loadFailed` is
  // set only by the timeout path and surfaces an explanatory message.
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const timeoutRef = useRef(null);

  const handleIframeLoad = useCallback(() => {
    setLoading(false);
    // onLoad indicates the browser finished navigating to the provider URL.
    // Clear the "could not load" timeout so we don't surface a false
    // positive warning once the iframe has actually rendered.
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setLoadFailed(false);
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoadFailed(true);
      setLoading(false);
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // `handleMessage` is intentionally light: the listener MUST be attached
  // to `window` (top-level) as soon as the component mounts, even before
  // the iframe load resolves, because providers can (and do) emit the
  // first postMessage during their own boot. `coverage-like` messages are
  // logged even when rejected so the dev tools console tells us *why*
  // they were dropped (origin drift, shape mismatch, unknown status…).
  const handleMessage = useCallback((event) => {
    const data = event.data;
    const looksLikeCoverage =
      data && typeof data === "object" &&
      (data.type === "coverage" ||
        ["available", "unavailable", "error"].includes(data.status));

    // 1) Strict origin check — only the provider portal (derived from
    //    COVERAGE_IFRAME_SRC so the two cannot drift).
    if (event.origin !== COVERAGE_ALLOWED_ORIGIN) {
      if (looksLikeCoverage) {
        // Something that *looks* like a coverage event came from an
        // unexpected origin. Log loudly so we can fix the allow-list.
        // eslint-disable-next-line no-console
        console.warn("[coverage] rejected origin", {
          received: event.origin,
          expected: COVERAGE_ALLOWED_ORIGIN,
          data,
        });
      }
      // Silent drop for noise (devtools extensions, unrelated iframes…).
      return;
    }

    // 2) Shape check — only the documented coverage payload.
    const isObject = !!(data && typeof data === "object");
    const isCoverageType = isObject && data.type === "coverage";
    const isValidStatus =
      isCoverageType && ["available", "unavailable", "error"].includes(data.status);

    if (!isCoverageType || !isValidStatus) {
      // eslint-disable-next-line no-console
      console.debug("[coverage] message ignored", {
        origin: event.origin,
        isObject,
        isCoverageType,
        isValidStatus,
        status: isObject ? data.status : undefined,
        data,
      });
      return;
    }

    // Accepted. Embedding clearly works, so clear the load warning.
    setLoadFailed(false);
    setLoading(false);
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }

    // Provider docs: the official payload uses `id` (normally GESCAL) for
    // availables and omits address/vertical/id for unavailable/error.
    // We also accept `gescal` defensively in case the provider renames it.
    let next = EMPTY_COVERAGE;
    if (data.status === "available") {
      next = {
        status: "available",
        address: data.address || "",
        vertical: data.vertical || "",
        gescal: data.gescal || data.id || "",
      };
    } else if (data.status === "unavailable") {
      next = { status: "unavailable", address: "", vertical: "", gescal: "" };
    } else if (data.status === "error") {
      next = { status: "error", address: "", vertical: "", gescal: "" };
    }

    // eslint-disable-next-line no-console
    console.debug("[coverage] accepted", { origin: event.origin, data, normalized: next });

    setResult(next);
    if (typeof onResult === "function") onResult(next);
  }, [onResult]);

  // Attach the listener on `window` (top-level) as early as possible — the
  // provider may emit its first postMessage before our iframe finishes
  // loading. Log readiness once so the browser console makes it obvious
  // the listener is alive.
  //
  // Note on the Likes provider (2026-02): real-flow testing showed the
  // iframe does NOT emit the documented postMessage when the user reaches
  // "¡Cobertura Confirmada!". The manual capture path inside the opportunity
  // modal is therefore the operational flow until the provider ships it.
  // The accepted/rejected diagnostics below stay for the day postMessage
  // does start arriving.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug("[coverage] listener ready", {
      iframeSrc: COVERAGE_IFRAME_SRC,
      expectedOrigin: COVERAGE_ALLOWED_ORIGIN,
    });
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  const resetResult = () => {
    setResult(EMPTY_COVERAGE);
    if (typeof onResult === "function") onResult(EMPTY_COVERAGE);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
      {/* ─── Iframe panel ─── */}
      <div
        className="rounded-lg overflow-hidden border border-[#1E1E5F] bg-[#0F0F3D] relative"
        style={{ minHeight: iframeHeight }}
        data-testid="coverage-iframe-wrapper"
      >
        {loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0F0F3D]/80 z-10"
            data-testid="coverage-iframe-loading"
          >
            <Loader2 className="h-8 w-8 text-[#03BEE4] animate-spin" />
            <p className="text-sm text-slate-400">Cargando buscador de cobertura…</p>
          </div>
        )}
        <iframe
          title="Buscador de Cobertura"
          src={COVERAGE_IFRAME_SRC}
          onLoad={handleIframeLoad}
          style={{ border: "none", width: "100%", height: iframeHeight, display: "block" }}
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          allow="geolocation"
        />
        {loadFailed && !result.status && (
          <div
            className="absolute inset-x-0 bottom-0 bg-[#0A0A35]/95 border-t border-[#1E1E5F] p-3 flex items-center gap-2 z-20"
            data-testid="coverage-iframe-fallback"
          >
            <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm text-slate-300">No se pudo cargar el buscador en este entorno.</span>
          </div>
        )}
      </div>

      {/* ─── Result panel ─── */}
      <div
        className="rounded-lg border border-[#1E1E5F] bg-[#0F0F3D] p-4 space-y-3 self-start"
        data-testid="coverage-result-panel"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-heading font-semibold text-white">Resultado</h2>
          {result.status && (
            <button
              type="button"
              onClick={resetResult}
              className="text-xs text-slate-400 hover:text-[#03BEE4]"
              data-testid="coverage-reset-btn"
            >
              Limpiar
            </button>
          )}
        </div>

        {!result.status && (
          <p className="text-sm text-slate-400">
            Introduce una dirección en el buscador para ver la cobertura disponible.
          </p>
        )}

        {result.status === "available" && (
          <div className="space-y-3" data-testid="coverage-result-available">
            <Badge className="bg-[#42E695]/20 text-[#42E695] border border-[#42E695]/40 flex items-center gap-1.5 w-fit">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Cobertura disponible
            </Badge>
            <div className="space-y-2 text-sm">
              <div>
                <div className="text-xs uppercase text-slate-500">Dirección</div>
                <div className="text-white break-words" data-testid="coverage-result-address">
                  {result.address || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">Vertical</div>
                <div className="text-white break-words" data-testid="coverage-result-vertical">
                  {result.vertical || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">GESCAL / ID</div>
                <div className="text-white font-mono text-xs break-all" data-testid="coverage-result-gescal">
                  {result.gescal || "—"}
                </div>
              </div>
            </div>
          </div>
        )}

        {result.status === "unavailable" && (
          <div className="space-y-2" data-testid="coverage-result-unavailable">
            <Badge className="bg-slate-600/20 text-slate-300 border border-slate-500/40 flex items-center gap-1.5 w-fit">
              <XCircle className="h-3.5 w-3.5" />
              Sin cobertura disponible
            </Badge>
            <p className="text-sm text-slate-400">
              No hay cobertura de fibra para la dirección consultada.
            </p>
          </div>
        )}

        {result.status === "error" && (
          <div className="space-y-2" data-testid="coverage-result-error">
            <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 flex items-center gap-1.5 w-fit">
              <AlertCircle className="h-3.5 w-3.5" />
              Error al consultar cobertura
            </Badge>
            <p className="text-sm text-slate-400">
              El portal de cobertura ha devuelto un error. Vuelve a intentarlo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoverageSearchEmbed;
